import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Download, ShieldAlert, Trash2, ShieldCheck } from "lucide-react";
import { adminLookupMember, adminExportMember, adminPurgeMember, adminRecentPurges } from "@/lib/api";
import { Card, SectionTitle, KawaiiModal } from "./kit";

const COUNT_LABELS = {
    tasks: "tasks", habits: "habits", reminders: "reminders", list_items: "list items",
    settings: "settings", chat_messages: "chat messages", llm_usage: "AI usage rows",
};

// Privacy tools: find ANY member by email, hand them their data, or erase
// them completely (irreversible — retype their email to confirm).
export default function MembersSection() {
    const [email, setEmail] = useState("");
    const [member, setMember] = useState(null);
    const [busy, setBusy] = useState(false);
    const [purgeOpen, setPurgeOpen] = useState(false);
    const [confirmEmail, setConfirmEmail] = useState("");
    const [notify, setNotify] = useState(true);
    const [purges, setPurges] = useState([]);

    const loadPurges = async () => {
        try { setPurges(await adminRecentPurges()); } catch { /* auth gate */ }
    };
    useEffect(() => { loadPurges(); }, []);

    const lookup = async (e) => {
        e?.preventDefault();
        const q = email.trim().toLowerCase();
        if (!q) return;
        setBusy(true);
        try {
            setMember(await adminLookupMember(q));
        } catch (err) {
            setMember(null);
            toast.error(err.response?.data?.detail || "couldn't look that member up");
        } finally { setBusy(false); }
    };

    const exportMember = async () => {
        try {
            const data = await adminExportMember(member.user_id);
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `mochi-export-${member.email}-${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("export downloaded 🍡");
        } catch { toast.error("export failed — try again?"); }
    };

    const purge = async () => {
        setBusy(true);
        try {
            const res = await adminPurgeMember(member.user_id, confirmEmail.trim().toLowerCase(), notify);
            toast.success(`${member.email} erased — ${Object.values(res.counts).reduce((a, b) => a + b, 0)} records gone${res.notified ? ", farewell note sent" : ""} 🍃`);
            setPurgeOpen(false); setConfirmEmail(""); setMember(null); setEmail("");
            await loadPurges();
        } catch (err) {
            toast.error(err.response?.data?.detail || "couldn't purge that member");
        } finally { setBusy(false); }
    };

    const confirmMatches = member && confirmEmail.trim().toLowerCase() === (member.email || "").toLowerCase();

    return (
        <div className="mt-10" data-testid="admin-members">
            <SectionTitle hint="Privacy requests: find any member by email, hand over their data, or erase them completely.">
                <ShieldAlert className="w-4 h-4 inline mr-1.5 -mt-0.5 text-terracotta" /> Member privacy tools
            </SectionTitle>

            <Card className="p-5">
                <form onSubmit={lookup} className="flex gap-2 flex-wrap">
                    <div className="relative flex-1 min-w-[240px]">
                        <Search className="w-4 h-4 text-warm-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                            placeholder="member's email…"
                            className="w-full pl-9 pr-4 py-2.5 rounded-full bg-surface border border-warm-300/40 text-sm text-warm-900 focus:outline-none focus:ring-2 focus:ring-terracotta/40"
                            data-testid="member-lookup-input"
                        />
                    </div>
                    <button type="submit" disabled={busy || !email.trim()}
                        className="px-5 py-2.5 rounded-full bg-terracotta text-cream text-sm font-semibold disabled:opacity-50"
                        data-testid="member-lookup-btn">
                        Find member
                    </button>
                </form>

                {member && (
                    <div className="mt-5 rounded-2xl bg-surface p-4" data-testid="member-card">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <p className="font-semibold text-warm-900" data-testid="member-card-name">{member.display_name}</p>
                                <p className="text-sm text-warm-500" data-testid="member-card-email">{member.email}</p>
                                <p className="text-xs text-warm-500 mt-1">
                                    joined {member.created_at ? new Date(member.created_at).toLocaleDateString() : "—"}
                                    {member.is_beta && <span className="ml-2 rounded-full bg-sage/25 px-2 py-0.5 text-[10px] font-semibold text-warm-900">beta</span>}
                                    {member.protected && (
                                        <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-mustard-soft px-2 py-0.5 text-[10px] font-semibold text-warm-900" data-testid="member-protected-badge">
                                            <ShieldCheck className="w-3 h-3" /> owner / admin — can&apos;t be purged
                                        </span>
                                    )}
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={exportMember}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold text-warm-900 bg-cream hover:bg-cream/70"
                                    data-testid="member-export-btn">
                                    <Download className="w-3.5 h-3.5" /> Export JSON
                                </button>
                                <button onClick={() => { setConfirmEmail(""); setPurgeOpen(true); }} disabled={member.protected}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold text-cream bg-terracotta hover:opacity-90 disabled:opacity-40"
                                    data-testid="member-purge-btn">
                                    <Trash2 className="w-3.5 h-3.5" /> Purge
                                </button>
                            </div>
                        </div>
                        <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-warm-800" data-testid="member-card-counts">
                            {Object.entries(member.counts).map(([k, v]) => (
                                <li key={k}><span className="font-semibold">{v}</span> {COUNT_LABELS[k] || k}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </Card>

            {purges.length > 0 && (
                <Card className="p-5 mt-4" data-testid="purge-audit-list">
                    <p className="text-warm-500 text-xs uppercase tracking-wide font-semibold">Recent erasures</p>
                    <ul className="mt-2 space-y-1">
                        {purges.map((p) => (
                            <li key={`${p.target_user_id}-${p.at}`} className="text-xs text-warm-800 flex flex-wrap gap-x-2">
                                <span>{new Date(p.at).toLocaleString()}</span>
                                <span className="text-warm-500">·</span>
                                <span>{p.reason === "self_service" ? "member deleted themselves" : `erased by ${p.by}`}</span>
                                <span className="text-warm-500">·</span>
                                <span>{Object.values(p.counts || {}).reduce((a, b) => a + b, 0)} records</span>
                                <span className="text-warm-500 font-mono truncate max-w-[180px]" title="email hash (no address kept)">#{(p.email_hash || "").slice(0, 10)}</span>
                            </li>
                        ))}
                    </ul>
                </Card>
            )}

            <KawaiiModal open={purgeOpen} onClose={() => !busy && setPurgeOpen(false)} title="Erase this member for good?" testid="member-purge-modal">
                <p className="text-sm text-warm-800 leading-relaxed">
                    This permanently deletes <span className="font-semibold">{member?.email}</span>&apos;s account and every
                    record — tasks, habits, reminders, lists, chat, stats, Mochi. Their email is scrubbed from invite
                    and allowlist records and any signed-in device is signed out immediately. <span className="text-terracotta">There is no undo.</span>
                </p>
                <label className="block mt-4 text-xs font-semibold text-warm-800">Type their email to confirm</label>
                <input
                    type="email" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)}
                    placeholder={member?.email || ""} autoComplete="off"
                    className="mt-1 w-full px-4 py-2.5 rounded-2xl bg-surface border border-warm-300/40 text-sm text-warm-900 focus:outline-none focus:ring-2 focus:ring-terracotta/40"
                    data-testid="member-purge-confirm-input"
                />
                <label className="mt-3 flex items-center gap-2 text-xs text-warm-800">
                    <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="member-purge-notify-checkbox" />
                    send Mochi&apos;s &quot;your account has been erased&quot; note to them first
                </label>
                <div className="mt-5 flex gap-2 justify-end">
                    <button onClick={() => setPurgeOpen(false)} disabled={busy}
                        className="px-4 py-2 rounded-full text-sm text-warm-500 hover:text-warm-900"
                        data-testid="member-purge-cancel-btn">
                        cancel
                    </button>
                    <button onClick={purge} disabled={busy || !confirmMatches}
                        className="px-5 py-2 rounded-full bg-terracotta text-cream text-sm font-semibold disabled:opacity-40"
                        data-testid="member-purge-confirm-btn">
                        {busy ? "erasing…" : "Erase everything"}
                    </button>
                </div>
            </KawaiiModal>
        </div>
    );
}
